import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Briefcase, Plus, Edit, Trash2, ExternalLink, RefreshCcw,
  Search, Eye, EyeOff, Building2, MapPin, Clock, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const JobsManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    description: '',
    salary: '',
    job_type: 'full-time',
    application_url: '',
    source: 'manual'
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/admin/jobs${params}`, { withCredentials: true });
      setJobs(response.data.jobs || []);
    } catch (error) {
      toast.error('İlanlar yüklenemedi');
    }
    setLoading(false);
  };

  const handleScrape = async () => {
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/jobs/scrape`, {}, { withCredentials: true });
      toast.success(`Scraping tamamlandı! ${response.data.results?.total_added || 0} yeni ilan eklendi`);
      loadJobs();
    } catch (error) {
      toast.error('Scraping başarısız');
    }
    setScraping(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingJob) {
        await axios.put(`${API}/admin/jobs/${editingJob.id}`, formData, { withCredentials: true });
        toast.success('İlan güncellendi');
      } else {
        await axios.post(`${API}/admin/jobs`, formData, { withCredentials: true });
        toast.success('İlan oluşturuldu');
      }
      setShowModal(false);
      setEditingJob(null);
      resetForm();
      loadJobs();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      description: job.description || '',
      salary: job.salary || '',
      job_type: job.job_type || 'full-time',
      application_url: job.application_url || '',
      source: job.source || 'manual'
    });
    setShowModal(true);
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Bu ilanı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, { withCredentials: true });
      toast.success('İlan silindi');
      loadJobs();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleToggleActive = async (jobId) => {
    try {
      const response = await axios.post(`${API}/admin/jobs/${jobId}/toggle-active`, {}, { withCredentials: true });
      toast.success(response.data.message);
      loadJobs();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company: '',
      location: '',
      description: '',
      salary: '',
      job_type: 'full-time',
      application_url: '',
      source: 'manual'
    });
  };

  const getSourceBadge = (source) => {
    const badges = {
      'kariyer': { bg: 'bg-blue-100', text: 'text-blue-700' },
      'indeed': { bg: 'bg-purple-100', text: 'text-purple-700' },
      'linkedin': { bg: 'bg-cyan-100', text: 'text-cyan-700' },
      'manual': { bg: 'bg-amber-100', text: 'text-amber-700' }
    };
    return badges[source] || badges['manual'];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg text-white">
            <Briefcase className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">İş İlanları Yönetimi</h2>
            <p className="text-sm text-gray-500">{jobs.length} ilan</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleScrape}
            disabled={scraping}
            className="gap-2"
          >
            <Globe className={`w-4 h-4 ${scraping ? 'animate-spin' : ''}`} />
            {scraping ? 'Taranıyor...' : 'Web Tara'}
          </Button>
          <Button onClick={() => { resetForm(); setEditingJob(null); setShowModal(true); }} className="gap-2">
            <Plus className="w-4 h-4" />
            Yeni İlan
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="İlan ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadJobs()}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={loadJobs}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCcw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Henüz ilan yok</p>
            </div>
          ) : (
            <div className="divide-y">
              {jobs.map((job) => {
                const badge = getSourceBadge(job.source);
                return (
                  <div key={job.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium ${job.is_active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                            {job.title}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                            {job.source}
                          </span>
                          {!job.is_active && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Pasif</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {job.company}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location || 'Türkiye'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {job.views || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(job.id)}>
                          {job.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(job)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {job.application_url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={job.application_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(job.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>{editingJob ? 'İlanı Düzenle' : 'Yeni İlan Ekle'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Başlık *</label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Kabin Memuru"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Şirket *</label>
                    <Input
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      placeholder="Türk Hava Yolları"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Lokasyon</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="İstanbul"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-24"
                    placeholder="İlan detayları..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Maaş</label>
                    <Input
                      value={formData.salary}
                      onChange={(e) => setFormData({...formData, salary: e.target.value})}
                      placeholder="30,000 - 50,000 TL"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Çalışma Tipi</label>
                    <select
                      value={formData.job_type}
                      onChange={(e) => setFormData({...formData, job_type: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="full-time">Tam Zamanlı</option>
                      <option value="part-time">Yarı Zamanlı</option>
                      <option value="contract">Sözleşmeli</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Başvuru URL</label>
                  <Input
                    value={formData.application_url}
                    onChange={(e) => setFormData({...formData, application_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingJob(null); }}>
                    İptal
                  </Button>
                  <Button type="submit">
                    {editingJob ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JobsManagement;
